import { ErrorResponse } from '@aibees/shared/definition';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import useSearchPersonStore from '../store/search-person-store';
import { PeopleTableInfo } from '../types';

export function useUpdatePersonSearchItemsStore<T = PeopleTableInfo>(
  query: UseQueryResult<{ content: T[]; top?: T[] }, ErrorResponse>,
  isNotSearchTableQuery?: boolean
) {
  const { setPeopleSearchItems } = useSearchPersonStore();

  const memoizedData = useMemo(() => {
    if (!query.data?.content) return null;
    return {
      content: query.data.content,
      top: query.data.top,
      // Include other relevant fields that affect store updates
    };
  }, [query.data?.content, query.data?.top]); // Only when actual content changes

  useEffect(() => {
    if (!memoizedData || isNotSearchTableQuery) return;

    setPeopleSearchItems((currentItems) => {
      const existingIds = currentItems.map((item: PeopleTableInfo) => item.id);
      const newIds = memoizedData.content.map(
        (item: T) => (item as PeopleTableInfo).id
      );

      // Check if data has changed
      const isIdsChanged =
        existingIds.length === 0 ||
        existingIds.length !== newIds.length ||
        existingIds.some((id: string) => !newIds.includes(id)) ||
        newIds.some((id: string) => !existingIds.includes(id));

      if (isIdsChanged) {
        // console.log('🟢 Updating people store', memoizedData.content);
        const sortedTop = memoizedData.top || [];
        return [...sortedTop, ...memoizedData.content] as PeopleTableInfo[];
      }

      // console.log('🔵 No changes detected, returning same reference');
      return currentItems; // Same reference = no re-render
    });
  }, [memoizedData, isNotSearchTableQuery]);
}
